import { type FocusEvent, type RefObject, useCallback, useEffect, useLayoutEffect, useRef } from "react";

/** Where one open (or still closing) dialog will hand focus back to. */
type FocusDebt = { opener: HTMLElement | null };

/**
 * Every dialog still owed a focus return. A dialog that closes while another it opened is still up (a confirm that
 * opens the next step) passes its own opener on to that one, since the element the second was opened from is about
 * to unmount with the first.
 */
const debts = new Set<FocusDebt>();

/**
 * Hands focus back to whatever had it when a modal opened, once the modal has finished closing. Pair it with the
 * Modal's `disableRestoreFocus`, pass `onExited` to its transition and `onFocus` to the Modal.
 *
 * MUI's FocusTrap restores focus the moment `open` turns false, while the exit transition is still running and the
 * rest of the app (`#root`) is still `aria-hidden`. Focus then lands inside a hidden subtree, and Chrome blocks the
 * aria-hidden with "Blocked aria-hidden on an element because its descendant retained focus". MUI releases the modal
 * (and lifts aria-hidden) before it calls the transition's own `onExited`, so handing focus back there finds nothing
 * hidden around the opener.
 *
 * The opener is the one MUI's trap would have restored: where focus came from as it first entered the dialog. That is
 * not always what had focus as `open` turned true: a dialog opened from a menu item opens as the menu closes, and the
 * menu first puts focus back on its own trigger (the item itself unmounts). The element focused as it opened is kept
 * only for a dialog focus never entered.
 *
 * Focus goes back only while it is still on the page's body or inside the dialog, so a caller that moved it somewhere
 * on purpose keeps it there, and only to an opener still on the page. The view stays where it was, even if the
 * opener has moved out of it. A dialog unmounted while open (a caller that
 * renders it conditionally) has no exit transition: its focus is handed back once the unmount has finished.
 */
export const useDialogFocusReturn = (
	open: boolean,
	dialogRef: RefObject<HTMLElement | null>,
): { onExited: () => void; onFocus: (event: FocusEvent<HTMLElement>) => void } => {
	// Open, or closing and not yet exited: focus is still owed.
	const debtRef = useRef<FocusDebt | null>(null);
	const enteredRef = useRef(false);

	// A layout effect of the dialog's owner runs before the dialog's content exists: MUI's Portal mounts it a render
	// later. Nothing inside it (a callback ref focusing Cancel, the trap's own effect) can have taken focus yet.
	useLayoutEffect(() => {
		// Reopened before it finished closing: focus is still owed to the first opener, not to the closing dialog.
		if (!open || debtRef.current !== null) return;
		const debt: FocusDebt = { opener: document.activeElement instanceof HTMLElement ? document.activeElement : null };
		debtRef.current = debt;
		debts.add(debt);
		enteredRef.current = false;
	}, [open]);

	// The Modal's root sees every focus inside it, its content's callback refs included (React finds the handler before
	// the root's own ref is attached), so the first is where focus entered from.
	const onFocus = useCallback((event: FocusEvent<HTMLElement>) => {
		const debt = debtRef.current;
		if (debt === null || enteredRef.current) return;
		enteredRef.current = true;
		const from = event.relatedTarget;
		if (from instanceof HTMLElement && !event.currentTarget.contains(from)) debt.opener = from;
	}, []);

	const handBack = useCallback(() => {
		const debt = debtRef.current;
		if (debt === null) return;
		debtRef.current = null;
		debts.delete(debt);
		const { opener } = debt;
		const dialog = dialogRef.current;
		if (dialog !== null) {
			for (const other of debts) {
				if (other.opener !== null && dialog.contains(other.opener)) other.opener = opener;
			}
		}
		if (opener === null || !opener.isConnected) return;
		const active = document.activeElement;
		// DEFAULT: not inside — after an unmount the dialog's node is gone, and focus has fallen to the body anyway.
		const unclaimed = active === null || active === document.body || (dialog?.contains(active) ?? false);
		// Without scrolling: what the dialog did can move its opener (an ended candidate's card settles at the bottom of
		// its lane), and following it there would drop the view the user was working in.
		if (unclaimed) opener.focus({ preventScroll: true });
	}, [dialogRef]);

	// Unmounted while open or still closing: every cleanup of this commit (MUI's lifting aria-hidden among them) has
	// run by the next microtask. Checked again then, since a cleanup isn't always an unmount: StrictMode (and a
	// reused subtree) runs the cleanup and the setup back to back on a dialog that stays open.
	const mountedRef = useRef(false);
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			queueMicrotask(() => {
				if (!mountedRef.current) handBack();
			});
		};
	}, [handBack]);

	return { onExited: handBack, onFocus };
};
